import type { MeleeFragment } from '@/graphql/api'
import { GemsApi_StoneCategory, StoneCategoryCode } from '@/graphql/api'
import { computed, type Ref } from 'vue'
import { assertNever, isNotUndefinedOrNull } from '@/helpers'

type MeleeInfo = {
    totalCaratWeight: number
    size?: string
    color?: MeleeFragment['melee']['colors']
    clarity?: MeleeFragment['melee']['clarities']
    grade?: MeleeFragment['melee']['grade']
}

function getMeleeInfo(melees: MeleeFragment[]): MeleeInfo | undefined {
    const firstMelee = melees[0]

    if (!firstMelee) {
        return undefined
    }

    const totalCaratWeight = melees.reduce((total, item) => {
        if (
            item.melee.grade?.category.systemCode !== StoneCategoryCode.Gemstone
        ) {
            return total + item.quantity * Number(item.melee.caratWeight)
        }

        return total
    }, 0)

    const sizeEqual = melees.every(
        (item) => item.melee.width === firstMelee.melee.width
    )
    const size = sizeEqual ? firstMelee.melee.width : undefined

    const uniqueSystemCodePredicate = <T extends { systemCode: string }[]>(
        item: T[number],
        index: number,
        self: T
    ) => self.findIndex((c) => c.systemCode === item.systemCode) === index

    const categories = melees
        .map((melee) => melee.melee.grade?.category)
        .filter(isNotUndefinedOrNull)
        .filter(uniqueSystemCodePredicate)

    const getColor = (melees: MeleeFragment[]) =>
        melees
            .map((melee) => melee.melee.colors)
            .flat()
            .filter(uniqueSystemCodePredicate)

    const getClarity = (melees: MeleeFragment[]) =>
        melees
            .map((melee) => melee.melee.clarities)
            .flat()
            .filter(uniqueSystemCodePredicate)

    const grade = firstMelee.melee.grade || undefined

    if (categories.length === 1 && categories[0]) {
        const color = getColor(melees)
        const clarity = getClarity(melees)

        const category = categories[0]

        switch (category.systemCode) {
            case GemsApi_StoneCategory.Diamond:
                if (color.length === 1 && clarity.length === 1) {
                    return {
                        totalCaratWeight,
                        color,
                        clarity,
                    }
                }

                return {
                    totalCaratWeight,
                }
            case GemsApi_StoneCategory.FancyDiamond:
                if (color.length === 1 && clarity.length === 1) {
                    return {
                        totalCaratWeight,
                        color,
                        clarity,
                        grade,
                    }
                }
                return {
                    totalCaratWeight,
                }
            case GemsApi_StoneCategory.Gemstone:
                if (color.length === 1 && clarity.length === 1) {
                    return {
                        totalCaratWeight,
                        size,
                    }
                }
                return {
                    totalCaratWeight,
                }
            case GemsApi_StoneCategory.LabGrownDiamond:
                // TODO Why didn't we have this case before???
                return undefined
            default:
                return assertNever(category.systemCode, undefined)
        }
    } else {
        if (
            categories.some(
                (category) =>
                    category.systemCode === GemsApi_StoneCategory.Diamond
            )
        ) {
            const diamondMelees = melees.filter(
                (item) =>
                    item.melee.grade?.category.systemCode ===
                    GemsApi_StoneCategory.Diamond
            )

            return {
                totalCaratWeight,
                size,
                color: getColor(diamondMelees),
                clarity: getClarity(diamondMelees),
            }
        }

        if (
            categories.some(
                (category) => category.systemCode === StoneCategoryCode.Gemstone
            ) &&
            categories.some(
                (category) =>
                    category.systemCode === StoneCategoryCode.FancyDiamond
            )
        ) {
            const fancyDiamondMelees = melees.filter(
                (item) =>
                    item.melee.grade?.category.systemCode ===
                    StoneCategoryCode.FancyDiamond
            )

            return {
                totalCaratWeight,
                size,
                color: getColor(fancyDiamondMelees),
                clarity: getClarity(fancyDiamondMelees),
                grade,
            }
        }

        const color = getColor(melees)
        const clarity = getClarity(melees)

        return {
            totalCaratWeight,
            size,
            color,
            clarity,
        }
    }
}

export function useMelee(
    meleeItems: Readonly<Ref<MeleeFragment[] | undefined | null>>
) {
    const melee = computed(
        () => meleeItems.value && getMeleeInfo(meleeItems.value)
    )
    const meleeColorAndClarity = computed(() => {
        const color = melee.value?.color
            ?.map((color) => color.translation.name)
            .join('-')
        const clarity = melee.value?.clarity
            ?.map((clarity) => clarity.translation.name)
            .join('-')

        return [color, clarity].filter(Boolean).join(' / ')
    })

    const meleeTotalCaratWeight = computed(() => {
        if (melee.value?.totalCaratWeight) {
            return melee.value.totalCaratWeight.toFixed(2)
        }
        return undefined
    })

    const meleeSize = computed(() => melee.value?.size)

    return {
        meleeTotalCaratWeight,
        meleeColorAndClarity,
        meleeSize,
    }
}
